// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-127 — Modal de alta/edición de administradores de Brillamos.
//
// Un solo formulario para dos flujos: agregar un administrador nuevo (buscando a un colaborador
// ya sincronizado desde AD) y editar uno existente. Ambos terminan en la MISMA mutación
// (PUT v1/users/{id}/recognition-role) — el backend no exige que el usuario ya tenga un rol de
// Brillamos, solo que sus roles ACTUALES estén dentro de lo que el actor puede otorgar
// (RoleGrantPolicy.CanManageUser). AdminGlobal nunca se gestiona desde aquí.
//
// Asignación masiva (minuta 2026-10-20): el alta acepta n personas (AdminRolePeoplePicker) y les
// aplica a todas el mismo rol, alcance, estado y permisos, una llamada por persona. La edición
// sigue siendo de una sola persona.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, InitialsAvatar, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/common';
import type { UserSearchSelection } from '@/components/common';
import { OuTreeSelect } from '@/components/common/OuTree';
import type { OUItem } from '@/api/types';
import { grantableRoles } from '@/auth/roleGrants';
import type { UserRole } from '@/hooks/useCurrentUser';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import {
  useUpdateUserRoles,
  permissionsForRole,
  PERMISSION_ORDER,
  ALL_FALSE,
  applyReadOnlyRule,
  hasReadOnlyWriteConflict,
  countsAsWriteForReadOnlyRule,
  getRecognitionErrorMessage,
  getRecognitionErrorStatus,
} from '@/api/recognition';
import type {
  AdminUserDto,
  AdminRoleKey,
  PermissionKey,
  PermissionMap,
  UpdateUserRolesBody,
} from '@/api/recognition';
import { AdminSaveBar } from './adminShared';
import { AdminRolePeoplePicker } from './AdminRolePeoplePicker';
import { ejecutarEnLote } from './asignacionMasiva';

/** Roles asignables desde esta pantalla. AdminGlobal NO se asigna aquí — si hace falta darle un
 *  rol distinto a un AdminGlobal, se hace desde la Administración General (fuera de esta pantalla). */
// AdminGlobal no se ofrece: el rol raiz no se otorga desde esta pantalla.
// AdminOU (2026-09-25) si, para poder concederle permisos del programa sin convertirlo en
// administrador de el. Su plantilla esta vacia, asi que elegirlo no marca ninguna casilla.
// 2026-11-02 — pero ya NO se ofrece al DAR DE ALTA («Agregar administrador», pedido de QA): solo sigue
// en el selector de quien YA tiene ese rol, para poder editar sus permisos sin perderlo. Ver
// `rolesDelSelector`.
const ROLE_KEYS: AdminRoleKey[] = ['AdminBrillamos', 'AdminOUBrillamos', 'AdminOU'];

/** Claves redundantes/muertas que se OMITEN como checkbox en el modal (petición de la clienta —
 *  fusión de permisos duplicados). En ambos casos el permiso oculto NO gatea nada en el front ni
 *  ninguna capacidad de `CapabilityPolicy` en el back; el permiso que SÍ gatea la pantalla se
 *  conserva (renombrado en i18n):
 *   - `manageUsers`  → fusionado en `manageRoles` ("Configurar roles y permisos"). La capacidad
 *     `AdministrarUsuariosRolesPermisos` se resuelve solo con `manageRoles`.
 *   - `manageCampaigns` → fusionado en `createCampaigns` ("Crear y gestionar campañas"), que es el
 *     único que gatea el menú Campañas (`auth/accessMatrix.ts`).
 *   - `viewAnalytics` → Reconocimiento NO lo consume (ningún guard/menú/botón). La analítica que sí
 *     se controla por permiso es la de Pulso, con el flag legacy `canViewAnalytics` en su propio
 *     modal (`AdminView/tabs/RolesPulsoTab`). Aquí solo confundía, así que se oculta.
 *   - `manageRecognitions` → gateaba la sección "Reconocimientos" (`AdminCatalogo`, puerta de solo
 *     lectura al catálogo de Pulso). La sección se retiró del menú por completo (decisión de Adriana,
 *     2026-08-23): el componente y su entrada en `sections.tsx`/`accessMatrix.ts` ya no existen, así
 *     que el permiso queda huérfano y se sigue ocultando del modal.
 *  Se dejan las claves intactas en `PERMISSION_ORDER`/`PermissionMap` (espejo de SharedContracts) y
 *  en el payload; solo se ocultan del catálogo editable. Si el backend llega a deprecarlas, este es
 *  el punto a alinear. (Puntos: `managePoints`/`managePointsConfig` NO se fusionan por ahora — gatean
 *  pantallas distintas, Gestión vs Puntos; solo se renombraron para dejar claro qué abre cada uno.) */
const HIDDEN_PERMISSIONS: ReadonlySet<PermissionKey> = new Set(['manageUsers', 'manageCampaigns', 'viewAnalytics', 'manageRecognitions']);
const DISPLAYED_PERMISSIONS: PermissionKey[] = PERMISSION_ORDER.filter(key => !HIDDEN_PERMISSIONS.has(key));

const TITLE_ID = 'adm-role-editor-title';

/** Qué abrió el modal. `null` = cerrado. En 'edit' siempre viene una fila real de la tabla
 *  (nunca AdminGlobal — esa fila oculta su acción de editar). */
export type AdminEditorTarget = { mode: 'create' } | { mode: 'edit'; admin: AdminUserDto };

interface AdminRoleEditorModalProps {
  target: AdminEditorTarget | null;
  /** Lista completa de admins (sin el filtro de búsqueda de la tabla) — para detectar en el alta
   *  si la persona elegida ya es administrador de Brillamos. */
  existingAdmins: AdminUserDto[];
  ouItems: OUItem[];
  /** Roles del actor — decide qué rol(es) puede ofrecer el selector (espejo de
   *  RoleGrantPolicy.GrantableBy, ver `@/auth/roleGrants`). */
  activeRoles: UserRole[];
  onClose: () => void;
}

/** Resultado del último guardado del alta: cuántas personas se intentaron y cuáles fallaron. */
interface ResultadoLote {
  total: number;
  fallos: { persona: UserSearchSelection; mensaje: string }[];
}

/** Borrador inicial al agregar a la PRIMERA persona: si ya es admin de Brillamos se precargan sus
 *  datos reales (sin callejón sin salida); si no, en blanco y sin rol. */
function borradorInicial(persona: UserSearchSelection, existingAdmins: AdminUserDto[]): RoleDraft {
  const already = existingAdmins.find((admin) => admin.id === persona.id);
  if (already) {
    return { role: already.role, scopeOuIds: [...already.scopeOuIds], active: already.active, permissions: { ...already.permissions } };
  }
  return { role: null, scopeOuIds: [], active: true, permissions: { ...ALL_FALSE } };
}

interface RoleDraft {
  /** null hasta que el operador elige uno explícitamente — sin default, para no dejar guardable
   *  por accidente un alcance/plantilla que nadie decidió. */
  role: AdminRoleKey | null;
  scopeOuIds: string[];
  active: boolean;
  permissions: PermissionMap;
}

export function AdminRoleEditorModal({
  target, existingAdmins, ouItems, activeRoles, onClose,
}: Readonly<AdminRoleEditorModalProps>) {
  const { t } = useTranslation('recognition');
  const update = useUpdateUserRoles();
  /** Roles que el actor puede ofrecer en el selector — nunca todos los `ROLE_KEYS` a ciegas. Un
   *  Admin OU Reconocimiento solo se ofrece a sí mismo (ver roleGrants.ts); Admin General/Admin
   *  Reconocimiento siguen viendo ambos. */
  const assignableRoles = useMemo(
    () => ROLE_KEYS.filter((role) => grantableRoles(activeRoles).includes(role)),
    [activeRoles],
  );

  /** Persona en edición (modo 'edit'). */
  const [selectedUser, setSelectedUser] = useState<UserSearchSelection | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  /** Personas a las que se dará de alta (modo 'create'). */
  const [people, setPeople] = useState<UserSearchSelection[]>([]);
  const [progreso, setProgreso] = useState<{ hechos: number; total: number } | null>(null);
  const [resultado, setResultado] = useState<ResultadoLote | null>(null);
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  /** Estado de la cuenta al abrir la edición: `active` solo viaja si el administrador lo cambió. */
  const [cuentaAlAbrir, setCuentaAlAbrir] = useState<boolean | null>(null);
  const [isConfirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);

  useEffect(() => {
    setPeople([]);
    setProgreso(null);
    setResultado(null);
    setCuentaAlAbrir(target?.mode === 'edit' ? target.admin.active : null);
    setConfirmingDeactivate(false);
    // BUG (QA, 2026-08-23) — el error ("no tienes permiso") o el "Guardado" de un intento anterior
    // se quedaban pegados: `update` (useUpdateUserRoles) es la MISMA instancia de mutación durante
    // toda la vida del modal, y ni cerrarlo ni abrirlo para otro administrador (o para dar de alta
    // uno nuevo) reseteaba su `isError`/`isSuccess` — solo un nuevo `mutate()` lo hacía, así que el
    // resultado del intento anterior se veía instantáneamente en la ventana siguiente, antes de
    // cualquier acción nueva. Se resetea aquí, en el mismo efecto que ya limpia el resto del
    // formulario al cambiar de `target` (cerrar el modal, o abrirlo para otra persona/alta).
    update.reset();
    if (!target) {
      setSelectedUser(null);
      setActiveId(null);
      setDraft(null);
      setDirty(false);
      return;
    }
    if (target.mode === 'edit') {
      const admin = target.admin;
      setActiveId(admin.id);
      setSelectedUser({ id: admin.id, email: admin.email, displayName: admin.displayName ?? admin.email });
      setDraft({
        role: admin.role,
        scopeOuIds: [...admin.scopeOuIds],
        active: admin.active,
        permissions: { ...admin.permissions },
      });
    } else {
      setActiveId(null);
      setSelectedUser(null);
      setDraft(null);
    }
    setDirty(false);
    // `update.reset` es la función estable que expone TanStack Query para esta mutación (no cambia
    // de identidad entre renders); se declara igual para que el lint de deps no la dé por olvidada.
  }, [target, update.reset]);

  /** El borrador nace con la primera persona y se va con la última; agregar o quitar a alguien
   *  más no lo toca (todas reciben lo mismo), pero sí deja algo nuevo por guardar. */
  function handlePeopleChange(next: UserSearchSelection[]) {
    setResultado(null);
    setPeople(next);
    if (next.length === 0) {
      setDraft(null);
      setDirty(false);
      return;
    }
    if (people.length === 0 && next[0]) {
      setDraft(borradorInicial(next[0], existingAdmins));
      setDirty(false);
      return;
    }
    setDirty(true);
  }

  const applyRole = (role: AdminRoleKey) => {
    setDraft((current) => (current ? {
      ...current,
      role,
      permissions: permissionsForRole(role),
      scopeOuIds: role === 'AdminOUBrillamos' ? current.scopeOuIds : [],
    } : current));
    setDirty(true);
  };
  /**
   * RN-R03 — «Solo lectura» y los permisos de escritura se excluyen mutuamente.
   *
   * 🔴 La exclusión se aplica AL MARCAR, no al Guardar, y el criterio de qué cuenta como escritura
   * sale del contrato congelado (`countsAsWriteForReadOnlyRule`), no de una lista propia: el
   * servidor rechaza el conjunto en conflicto con 422 y una segunda definición aquí derivaría del
   * suyo en cuanto alguien añadiera un permiso.
   *
   * ⚠️ El servidor valida el estado FINAL una sola vez —a propósito, para no rechazar estados
   * intermedios legítimos—, así que aquí NO hace falta bloquear checkboxes: basta con que marcar
   * uno apague el otro. Deshabilitarlos obligaría a apagar «Solo lectura» antes de poder tocar nada
   * más, que es justo el paso que esto ahorra.
   */
  const togglePerm = (key: PermissionKey) => {
    setDraft((current) => {
      if (!current) return current;
      const marcado = { ...current.permissions, [key]: !current.permissions[key] };
      return { ...current, permissions: applyReadOnlyRule(marcado, key) };
    });
    setDirty(true);
  };
  const changeScopeOuIds = (ouIds: string[]) => {
    setDraft((current) => (current ? { ...current, scopeOuIds: ouIds } : current));
    setDirty(true);
  };
  /**
   * «Cuenta activa» es el acceso a la plataforma (`AppUser.IsActive`), no el rol de administrador.
   * Apagarla da de baja a la persona, así que se confirma; encenderla no.
   */
  const setCuentaActiva = (isActive: boolean) => {
    setDraft((current) => (current ? { ...current, active: isActive } : current));
    setDirty(true);
  };
  const toggleActive = () => {
    if (draft?.active) {
      setConfirmingDeactivate(true);
      return;
    }
    setCuentaActiva(true);
  };

  const isCreating = target?.mode === 'create';
  /** 2026-11-02 — «Admin OU Pulso» no se ofrece para dar de alta; solo aparece si la persona ya lo
   *  tiene (editar sus permisos sin que el selector la deje sin rol). */
  const rolesDelSelector = draft?.role === 'AdminOU'
    ? assignableRoles
    : assignableRoles.filter((role) => role !== 'AdminOU');
  const hasTarget = isCreating ? people.length > 0 : activeId !== null;
  const canSave = hasTarget && draft !== null && draft.role !== null;

  /** RoleGrantPolicy niega la escritura (p.ej. AdminOUBrillamos, que el backend deja ENTRAR a la
   *  pantalla pero nunca a guardar) devuelve 403 sin cuerpo — el mensaje genérico de guardado no lo
   *  distingue de una falla de red. Mismo criterio que StateBlock ya aplica a la carga de la lista. */
  const mensajeDeError = (error: unknown) => (getRecognitionErrorStatus(error) === 403
    ? t('admin.roles.saveForbidden')
    : getRecognitionErrorMessage(error, t('admin.saveError')));

  /** Alta de n personas: una llamada por persona, con concurrencia acotada. Las que fallan se
   *  quedan en la lista con su motivo para reintentar; las que salieron bien se quitan. */
  async function guardarLote(body: UpdateUserRolesBody) {
    const lote = [...people];
    setResultado(null);
    setProgreso({ hechos: 0, total: lote.length });
    const fallos = await ejecutarEnLote(
      lote,
      (persona) => update.mutateAsync({ id: persona.id, body }),
      (hechos) => setProgreso({ hechos, total: lote.length }),
    );
    setProgreso(null);
    setResultado({ total: lote.length, fallos: fallos.map((fallo) => ({ persona: fallo.item, mensaje: mensajeDeError(fallo.error) })) });
    if (fallos.length === 0) {
      setDirty(false);
      return;
    }
    setPeople(fallos.map((fallo) => fallo.item));
  }

  const save = () => {
    if (!canSave || !draft || !draft.role) return;
    // Sin `active` salvo cambio explícito de la cuenta en la edición (2026-10-21): antes se
    // mandaba siempre —en el alta masiva, el de la primera persona para el lote completo; en la
    // edición, el de la lista en caché— y cambiar solo permisos daba de baja a usuarios activos.
    const body: UpdateUserRolesBody = {
      role: draft.role,
      scopeOuIds: draft.scopeOuIds,
      permissions: draft.permissions,
    };
    if (!isCreating && cuentaAlAbrir !== null && draft.active !== cuentaAlAbrir) {
      body.isActive = draft.active;
    }
    if (isCreating) {
      void guardarLote(body);
      return;
    }
    if (!activeId) return;
    update.mutate({ id: activeId, body }, { onSuccess: () => setDirty(false) });
  };

  /**
   * Permisos de ESCRITURA que conviven hoy con «Solo lectura» en este borrador (RN-R03). Vacío en
   * cuanto el admin toca cualquier casilla, porque `togglePerm` deja el mapa ya sin conflicto: solo
   * puede venir de un documento guardado antes de que la regla existiera en la pantalla.
   */
  const conflictoHeredado: PermissionKey[] = draft && hasReadOnlyWriteConflict(draft.permissions)
    ? PERMISSION_ORDER.filter((k) => draft.permissions[k] && countsAsWriteForReadOnlyRule(k))
    : [];

  const saveErrorMessage = mensajeDeError(update.error);

  const adminsElegidos = isCreating ? people.filter((persona) => existingAdmins.some((admin) => admin.id === persona.id)) : [];
  const alreadyAdmin = people.length === 1 && adminsElegidos.length === 1;
  const isSaving = progreso !== null || update.isPending;
  const savedOk = isCreating ? resultado !== null && resultado.fallos.length === 0 : update.isSuccess;
  const titleText = target?.mode === 'edit'
    ? t('admin.roles.editTitle', { name: target.admin.displayName ?? target.admin.email })
    : t('admin.roles.addTitle');

  return (
    <>
    <Modal open={target !== null} onClose={onClose} maxWidth={640} labelledBy={TITLE_ID}>
      <ModalHeader onClose={onClose}><h2 id={TITLE_ID}>{titleText}</h2></ModalHeader>
      <ModalBody>
        {isCreating && (
          <AdminRolePeoplePicker
            people={people}
            onChange={handlePeopleChange}
            existingAdmins={existingAdmins}
            isDisabled={isSaving}
          />
        )}

        {draft && hasTarget && (
          <>
            {!isCreating && selectedUser && (
              <div className="adm-admin-cell">
                <InitialsAvatar
                  name={selectedUser.displayName || selectedUser.email || '?'}
                  baseClass="rp-avatar"
                  seed={selectedUser.id}
                />
                <div>
                  <div className="adm-admin-name">{selectedUser.displayName}</div>
                  <div className="adm-admin-email">{selectedUser.email}</div>
                </div>
              </div>
            )}
            {alreadyAdmin && (
              <p className="adm-role-note">{t('admin.roles.alreadyAdminNote')}</p>
            )}
            {people.length > 1 && adminsElegidos.length > 0 && (
              <p className="adm-role-note adm-role-note--warn">
                {t('admin.roles.alreadyAdminBulkNote', { count: adminsElegidos.length })}
              </p>
            )}
            <div className="adm-editor-grid">
              <label className="adm-field">
                <span>{t('admin.roles.colRole')}</span>
                <select className="field" value={draft.role ?? ''} onChange={(e) => applyRole(e.target.value as AdminRoleKey)}>
                  {draft.role === null && (
                    <option value="" disabled>{t('admin.roles.selectRolePlaceholder')}</option>
                  )}
                  {rolesDelSelector.map((role) => (
                    <option key={role} value={role}>{t(`admin.roles.role.${role}`)}</option>
                  ))}
                </select>
              </label>
              {/* Solo en la edición: el alta (individual o masiva) nunca toca la cuenta. */}
              {!isCreating && (
                <label className="adm-field adm-field--toggle" title={t('admin.roles.accountActiveHint')}>
                  <span>{t('admin.roles.accountActive')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={draft.active}
                    aria-label={t('admin.roles.accountActive')}
                    className={`adm-toggle ${draft.active ? 'adm-toggle--on' : ''}`}
                    onClick={toggleActive}
                  >
                    <span className="adm-toggle-knob" />
                  </button>
                </label>
              )}
            </div>

            {draft.role === 'AdminOUBrillamos' && (
              <div className="adm-field">
                <span>{t('admin.roles.colScope')}</span>
                <OuTreeSelect
                  items={ouItems}
                  mode="multi"
                  variant="inline"
                  value={draft.scopeOuIds}
                  onChange={changeScopeOuIds}
                />
              </div>
            )}

            <div className="adm-field">
              <span>{t('admin.roles.permMatrix')}</span>
              {/* 🔴 Un documento guardado ANTES de esta regla puede llegar ya en conflicto. No se
                  corrige solo al abrir el modal: eso escribiría una decisión que el admin no ha
                  tomado, y sobre un permiso que quizá ni ve. Se avisa, y el primer clic lo resuelve.
                  El aviso nombra los permisos en conflicto —incluidos los OCULTOS— porque si no,
                  con «Solo lectura» y un permiso oculto marcado, la pantalla no explicaría el 422
                  que el servidor va a devolver al Guardar. */}
              {conflictoHeredado.length > 0 && (
                <p className="adm-role-note adm-role-note--warn" role="alert">
                  <i className="fas fa-triangle-exclamation" aria-hidden="true" />{' '}
                  {t('admin.roles.readOnlyConflict', {
                    permisos: conflictoHeredado.map((k) => t(`admin.perm.${k}`)).join(', '),
                  })}
                </p>
              )}
              <div className="adm-perm-grid">
                {DISPLAYED_PERMISSIONS.map((key: PermissionKey) => (
                  <label key={key} className="adm-perm">
                    <input type="checkbox" checked={draft.permissions[key]} onChange={() => togglePerm(key)} />
                    <span>{t(`admin.perm.${key}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            {!isCreating && update.isError && (
              <p className="rp-error" role="alert">{saveErrorMessage}</p>
            )}
          </>
        )}

        {progreso && (
          <p className="adm-role-note" role="status">
            <i className="fas fa-spinner fa-spin" aria-hidden="true" />{' '}
            {t('admin.roles.bulkProgress', { done: progreso.hechos, total: progreso.total })}
          </p>
        )}
        {resultado && resultado.total > 1 && resultado.fallos.length === 0 && (
          <p className="adm-role-note" role="status">{t('admin.roles.bulkDone', { count: resultado.total })}</p>
        )}
        {resultado && resultado.fallos.length > 0 && (
          <div className="rp-error" role="alert">
            <p>{t('admin.roles.bulkPartial', { ok: resultado.total - resultado.fallos.length, total: resultado.total })}</p>
            <ul className="adm-bulk-errors">
              {resultado.fallos.map(({ persona, mensaje }) => (
                <li key={persona.id}><strong>{persona.displayName || persona.email}</strong>: {mensaje}</li>
              ))}
            </ul>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <AdminSaveBar
          onSave={save}
          saving={isSaving}
          dirty={dirty}
          disabledReason={!canSave && draft ? t('admin.roles.selectRoleFirst') : null}
          saved={savedOk}
          label={isCreating && people.length > 1 ? t('admin.roles.saveForPeople', { count: people.length }) : undefined}
        />
      </ModalFooter>
    </Modal>
    {isConfirmingDeactivate && target?.mode === 'edit' && (
      <ConfirmModal
        type="danger"
        title={t('admin.roles.deactivateAccountTitle')}
        message={t('admin.roles.deactivateAccountMsg', { name: target.admin.displayName ?? target.admin.email })}
        confirmText={t('admin.roles.deactivateAccountConfirm')}
        onConfirm={() => { setConfirmingDeactivate(false); setCuentaActiva(false); }}
        onClose={() => setConfirmingDeactivate(false)}
      />
    )}
    </>
  );
}

export default AdminRoleEditorModal;
